import { Link, useNavigate, useParams } from "react-router-dom";
import Image from "../components/Image/Image";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../../api/axiosInstance";
import { DictionaryItem } from "../DictionaryPage/DictionaryPage";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../context/AuthContext/AuthContext";
import { toast } from "react-toastify";

const SinglePost = () => {
  const fetchPost = async (id: string | undefined) => {
    const res = await api.get(
      // `${process.env.REACT_APP_BACKEND_ROUTE}Blog/posts/${id}`
      `Blog/posts/${id}`
    );
    return res.data;
  };
  const { id } = useParams();
  const { isPending, error, data } = useQuery({
    queryKey: ["post", id],
    queryFn: () => fetchPost(id),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const [categories, setCategories] = useState<DictionaryItem[]>([]);

  const fetchCategories = useCallback(async () => {
    const response = await api.get(`Dictionary/Category`);
    setCategories(response.data);
  }, []);

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const [filled, setFilled] = useState(false);

  useEffect(() => {
    if (contentRef.current) {
      const { offsetHeight, offsetWidth } = contentRef.current;
      setDimensions({ width: offsetWidth, height: offsetHeight });
    }
  }, [data]);

  const { isAdmin, userId } = useAuth();
  const isAuthor: boolean = userId === data?.data?.authorId;
  const canDelete: boolean = isAdmin || isAuthor;

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return api.delete(
        // `${process.env.REACT_APP_BACKEND_ROUTE}Blog/posts/${id}`
        `Blog/posts/${id}`
      );
    },
    onSuccess: () => {
      toast.success("პოსტი წაიშალა!");
      // queryClient.invalidateQueries({ queryKey: ["posts"] });
      queryClient.removeQueries({ queryKey: ["posts"], exact: false });

      navigate("/");
    },
    onError: () => {
      toast.error("პოსტის წაშლა ვერ მოხერხდა");
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  const toggleFeaturedMutation = useMutation({
    mutationFn: async () => {
      const res = await api.patch(
        // `${process.env.REACT_APP_BACKEND_ROUTE}Blog/posts/feature/${id}`
        `Blog/posts/feature/${id}`
      );
      return res.data;
    },
    onSuccess: (response) => {
      setFilled(response.data?.data?.isFeatured);
      queryClient.invalidateQueries({ queryKey: ["post", id] });
    },
    onError: () => {
      toast.error("ვერ მოხდა რჩეულებში დამატება");
    },
  });

  useEffect(() => {
    if (data?.data?.isFeatured !== undefined) {
      setFilled(data.data.isFeatured);
    }
  }, [data?.data?.isFeatured]);

  if (isPending) return <>Loading...</>;
  if (error) return <>{`Something went wrong! - ${error.message}`}</>;
  if (!data) return <>Post not found!</>;
  return (
    <div className="flex flex-col gap-8 p-8">
      {/* detail */}
      <div
        className="flex gap-8"
        style={{
          minWidth: "12rem",
        }}
      >
        <div ref={contentRef} className="lg:w-4/5 flex flex-col gap-8">
          <h1 className="text-xl md:text-3xl xl:text-4xl 2xl:text-5xl font-semibold">
            {data.data.title}
          </h1>
          <div className="flex flex-col items-start md:flex-row md:items-center gap-2 text-gray-400 text-sm">
            <span>
              <span>ავტორი: </span>
              <span>{data.data.authorName}</span>
            </span>
            <span className="hidden md:inline">•</span>
            <span>
              <span>თარიღი </span>
              {data.data.createdAt.slice(0, 10)}
            </span>
            <span className="hidden md:inline">•</span>
            <span>
              <span>კატეგორია: </span>
              <Link
                className="text-blue-800"
                to={`/blog/posts?category=${data.data.categoryName}`}
              >
                {data.data.categoryName}
              </Link>
            </span>
          </div>
          <p className="text-gray-500 font-medium">{data.data.abstract}</p>
        </div>

        {data.data.coverImgUrl && dimensions.height > 0 && (
          <div className="hidden lg:block w-1/5">
            <Image
              src={data.data.coverImgUrl}
              width={`${Math.round(dimensions.width * 0.25)}`}
              height={`${dimensions.height}`}
              className="rounded-2xl object-cover"
              alt="Cover image"
            />
          </div>
        )}
      </div>

      {/* content */}
      <div className="flex flex-col md:flex-row gap-8 justify-between">
        <div
          className="lg:text-lg flex flex-col gap-6 text-justify"
          dangerouslySetInnerHTML={{ __html: data.data.content }}
        />
        {/* menu */}
        <div
          className="px-4 h-max sticky top-8"
          style={{
            minWidth: "12rem",
          }}
        >
          <h1 className="mt-8 mb-4 text-sm font-medium">
            <b>კატეგორიები</b>
          </h1>
          <div className="flex flex-col gap-2 text-sm align-start">
            {/* <Link className="underline">All</Link> */}
            <Link to="/blog/posts" className="underline py-2">
              ყველა კატეგორია
            </Link>
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/blog/posts?category=${category.categoryName}`}
                className="underline py-2"
              >
                {category.categoryName}
              </Link>
            ))}
          </div>
          {canDelete && (
            <>
              <div
                className="flex items-center gap-2 py-2 text-sm cursor-pointer"
                onClick={handleDelete}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 50 50"
                  fill="red"
                  width="20px"
                  height="20px"
                >
                  <path d="M 21 2 C 19.354545 2 18 3.3545455 18 5 L 18 7 L 10.154297 7 A 1.0001 1.0001 0 0 0 9.984375 6.9863281 A 1.0001 1.0001 0 0 0 9.8398438 7 L 8 7 A 1.0001 1.0001 0 1 0 8 9 L 9 9 L 9 45 C 9 46.645455 10.354545 48 12 48 L 38 48 C 39.645455 48 41 46.645455 41 45 L 41 9 L 42 9 A 1.0001 1.0001 0 1 0 42 7 L 40.167969 7 A 1.0001 1.0001 0 0 0 39.841797 7 L 32 7 L 32 5 C 32 3.3545455 30.645455 2 29 2 L 21 2 z M 21 4 L 29 4 C 29.554545 4 30 4.4454545 30 5 L 30 7 L 20 7 L 20 5 C 20 4.4454545 20.445455 4 21 4 z M 11 9 L 18.832031 9 A 1.0001 1.0001 0 0 0 19.158203 9 L 30.832031 9 A 1.0001 1.0001 0 0 0 31.158203 9 L 39 9 L 39 45 C 39 45.554545 38.554545 46 38 46 L 12 46 C 11.445455 46 11 45.554545 11 45 L 11 9 z M 18.984375 13.986328 A 1.0001 1.0001 0 0 0 18 15 L 18 40 A 1.0001 1.0001 0 1 0 20 40 L 20 15 A 1.0001 1.0001 0 0 0 18.984375 13.986328 z M 24.984375 13.986328 A 1.0001 1.0001 0 0 0 24 15 L 24 40 A 1.0001 1.0001 0 1 0 26 40 L 26 15 A 1.0001 1.0001 0 0 0 24.984375 13.986328 z M 30.984375 13.986328 A 1.0001 1.0001 0 0 0 30 15 L 30 40 A 1.0001 1.0001 0 1 0 32 40 L 32 15 A 1.0001 1.0001 0 0 0 30.984375 13.986328 z" />
                </svg>
                <span>წაშლა</span>
                {deleteMutation.isPending && (
                  <span className="text-xs">...</span>
                )}
              </div>
            </>
          )}
          {isAdmin && (
            <div
              className="flex items-center gap-2 py-2 text-sm cursor-pointer"
              onClick={() => toggleFeaturedMutation.mutate()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 50 50"
                fill={filled ? "gold" : "none"}
                stroke="gold"
                strokeWidth="2"
                width="20px"
                height="20px"
                style={{ cursor: "pointer" }}
              >
                <path d="M25 2 L31 18 H48 L34 29 L39 46 L25 36 L11 46 L16 29 L2 18 H19 Z" />
              </svg>
              <span>დაამატე რჩეულებში</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SinglePost;
